"use client";

import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import Link from "next/link";
import "./our-story.css";

/* Images — public/assets/images/ se serve hoti hain (import nahi, seedha URL path) */
const IMG = "/assets/images";
const imgHero = `${IMG}/our-story-hero.jpg`;
const imgCta = `${IMG}/our-story-cta.jpg`;

const imgBeginning = `${IMG}/The-Beginning.jpg`;
const imgTransformation = `${IMG}/Transformation.jpg`;
const imgIndustryKnowledge = `${IMG}/Industry-Knowledge.jpg`;
const imgDataIntelligence = `${IMG}/Data-and-Intelligence.jpg`;
const imgGreaterValue = `${IMG}/Greater-Value.jpg`;

const imgRealBusiness = `${IMG}/Real-Business-Understanding.jpg`;
const imgIndustryExperience = `${IMG}/Industry-Experience-Across-Sectors.jpg`;
const imgTechDataAi = `${IMG}/Technology,-Data-and-AI.jpg`;
const imgOwnership = `${IMG}/People-Who-Take-Ownership.jpg`;

/* All styling lives in our-story.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400): CSSProperties =>
  ({ "--os-delay": `${Math.min(i * step, max)}ms` } as CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "people"
  | "building"
  | "globe"
  | "pin"
  | "bars"
  | "process"
  | "industry"
  | "data"
  | "team"
  | "tech"
  | "outcome"
  | "target"
  | "diamond"
  | "mark"
  | "play";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    people: (
      <>
        <circle cx="12" cy="7" r="2.8" />
        <circle cx="5" cy="9" r="2.2" />
        <circle cx="19" cy="9" r="2.2" />
        <path d="M7 19c.4-3.2 2.3-4.8 5-4.8s4.6 1.6 5 4.8M1.8 17c.3-2.4 1.6-3.6 3.4-3.6M22.2 17c-.3-2.4-1.6-3.6-3.4-3.6" />
      </>
    ),
    building: (
      <>
        <path d="M4 21V5l9-2v18M13 9h7v12M2 21h20" />
        <path d="M7.5 8h2M7.5 12h2M7.5 16h2M16 13h1M16 17h1" />
      </>
    ),
    globe: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.6 2.6 3.8 5.6 3.8 9S14.6 18.4 12 21c-2.6-2.6-3.8-5.6-3.8-9S9.4 5.6 12 3Z" />
      </>
    ),
    pin: (
      <>
        <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    bars: <path d="M5 20V12M11 20V4M17 20V9M3 20h18" />,
    process: (
      <>
        <path d="M3 17l5-5 4 4 8-9" />
        <path d="M15 7h5v5" />
      </>
    ),
    industry: (
      <>
        <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
        <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
      </>
    ),
    data: (
      <>
        <ellipse cx="12" cy="5.5" rx="7" ry="2.8" />
        <path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" />
      </>
    ),
    team: (
      <>
        <circle cx="12" cy="6" r="2.6" />
        <circle cx="5.5" cy="9" r="2.1" />
        <circle cx="18.5" cy="9" r="2.1" />
        <path d="M7.5 20c.4-3.4 2.1-5.2 4.5-5.2s4.1 1.8 4.5 5.2M2 18c.3-2.3 1.5-3.5 3.5-3.5M22 18c-.3-2.3-1.5-3.5-3.5-3.5" />
      </>
    ),
    tech: (
      <>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1" />
        <circle cx="12" cy="12" r="7" />
      </>
    ),
    outcome: (
      <>
        <path d="M12 3a9 9 0 1 0 9 9h-9V3Z" />
        <path d="M15 3.5A9 9 0 0 1 20.5 9H15V3.5Z" />
      </>
    ),
    target: (
      <>
        <circle cx="11" cy="13" r="8" />
        <circle cx="11" cy="13" r="4.5" />
        <circle cx="11" cy="13" r="1" />
        <path d="m12 12 8-8M16 4h4v4" />
      </>
    ),
    diamond: (
      <>
        <path d="M6 4h12l4 6-10 11L2 10l4-6Z" />
        <path d="M2 10h20M9 4l-2 6 5 11 5-11-2-6" />
      </>
    ),
    mark: (
      <>
        <circle cx="9" cy="12" r="6.5" strokeWidth="2.6" />
        <circle cx="17.5" cy="14.5" r="4" strokeWidth="2.6" />
      </>
    ),
    play: (
      <>
        <circle cx="12" cy="12" r="9.5" />
        <path d="m10 8.5 5.5 3.5-5.5 3.5v-7Z" fill="currentColor" />
      </>
    ),
  };

  return (
    <svg
      className="os-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const stats: { icon: IconKey; value: string; label: string; note?: string; text?: boolean }[] = [
  { icon: "people", value: "15+", label: "Years of Experience" },
  { icon: "building", value: "250+", label: "Enterprise Projects" },
  { icon: "globe", value: "10+", label: "Industries" },
  { icon: "people", value: "230+", label: "Professionals" },
  { icon: "pin", value: "3", label: "Geographies", note: "India | Africa | Middle East" },
  { icon: "bars", value: "Long-term", label: "Customer Relationships", text: true },
];

const timeline = [
  {
    year: "2009",
    title: "The Beginning",
    text: "Started with a belief that technology creates value when it serves real business needs.",
    image: imgBeginning,
  },
  {
    year: "2010 – 2015",
    title: "Transformation",
    text: "Implemented enterprise systems and supported business transformation across industries.",
    image: imgTransformation,
  },
  {
    year: "2016 – 2020",
    title: "Industry Knowledge",
    text: "Deepened our understanding of industries, processes and business context.",
    image: imgIndustryKnowledge,
  },
  {
    year: "2021 – 2023",
    title: "Data and Intelligence",
    text: "Brought data, analytics and emerging AI into enterprise solutions.",
    image: imgDataIntelligence,
  },
  {
    year: "2024 onwards",
    title: "Greater Value",
    text: "Combining enterprise experience with AI and intelligence to help businesses become more valuable.",
    image: imgGreaterValue,
  },
];

const learned: { icon: IconKey; tone: string; title: string; text: string }[] = [
  { icon: "process", tone: "purple", title: "Business Processes", text: "How functions connect and influence each other." },
  { icon: "industry", tone: "green", title: "Industry Context", text: "What drives performance in different industries." },
  { icon: "data", tone: "blue", title: "Data and Information", text: "How better data leads to better decisions." },
  { icon: "team", tone: "amber", title: "People and Skills", text: "How people and capability affect delivery and growth." },
  { icon: "tech", tone: "rose", title: "Technology and Platforms", text: "How technology enables when it is contextual and simple." },
  { icon: "outcome", tone: "cyan", title: "Outcomes and Value", text: "How to measure what really matters for the business." },
];

const different = [
  {
    title: "Real Business Understanding",
    text: "We start with the business question, not the technology.",
    image: imgRealBusiness,
  },
  {
    title: "Industry Experience Across Sectors",
    text: "Deep experience in manufacturing, CPG, utilities, energy, financial services and more.",
    image: imgIndustryExperience,
  },
  {
    title: "Technology, Data and AI",
    text: "Enterprise platforms, data and AI to unlock greater value.",
    image: imgTechDataAi,
  },
  {
    title: "People Who Take Ownership",
    text: "Long-term relationships built on trust, accountability and shared success.",
    image: imgOwnership,
  },
];

/* ============================== PAGE ============================== */

export default function OurStoryPage() {
  const ref = useScrollReveal();

  return (
    <div className="os-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="os-hero" data-purpose="hero-section" id="top">
          <div
            className="os-hero-bg"
            style={{ backgroundImage: `url(${imgHero})` }}
          ></div>
          <div className="os-hero-overlay"></div>

          <div className="os-hero-layout">
            <div className="os-hero-copy">
              <h1 className="os-hero-title" data-reveal style={delay(0)}>Our Story</h1>

              <p className="os-hero-subtitle" data-reveal style={delay(1, 100)}>
                <span>Built Through Experience.</span>
                <span>Evolving Through Intelligence.</span>
              </p>

              <p className="os-hero-text" data-reveal style={delay(2, 100)}>
                Since 2009, we have worked with enterprises to transform how they operate, compete
                and grow. What we have learned is becoming what we are.
              </p>

              <div className="os-actions os-hero-actions" data-reveal style={delay(3, 100)}>
                <Link href="#our-journey" className="fp-buttons fp-btn-primary">
                  Our Journey <span>→</span>
                </Link>
                <Link href="#watch-story" className="fp-buttons os-btn-outline">
                  <span className="os-play"><Icon name="play" /></span> Watch Our Story
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================== STATS ============================= */}
        <section className="os-stats-section" data-purpose="stats">
          <div className="op-container">
            <div className="os-stats" data-reveal>
              {stats.map((s) => (
                <div className="os-stat" key={s.label}>
                  <span className="os-stat-icon"><Icon name={s.icon} /></span>
                  <div className="os-stat-copy">
                    <div className={`os-stat-value${s.text ? " os-stat-value--text" : ""}`}>{s.value}</div>
                    <div className="os-stat-label">{s.label}</div>
                    {s.note && <div className="os-stat-note">{s.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ TIMELINE ============================ */}
        <section className="op-section" data-purpose="journey" id="our-journey">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Our Journey</div>
              <h2 className="sec-title">From Enterprise Projects to Enterprise Value</h2>
              <p className="sec-sub">Every engagement has taught us something deeper about how businesses work.</p>
            </div>

            <div className="os-tl-grid">
              {timeline.map((t, i) => (
                <article className="os-tl-item" key={t.year} data-reveal style={delay(i, 90)}>
                  <span className="os-tl-dot" aria-hidden="true"></span>
                  <div className="os-tl-year">{t.year}</div>
                  <h3 className="os-tl-title">{t.title}</h3>
                  <div className="os-tl-image">
                    <img src={t.image} alt={`${t.title} — ${t.year}`} loading="lazy" />
                  </div>
                  <p className="os-tl-text">{t.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ LEARNED ============================= */}
        <section className="op-section" data-purpose="learnings">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Our Learnings</div>
              <h2 className="sec-title">What We Have Learned</h2>
              <p className="sec-sub">Project experience has given us deeper insights into how enterprises create value.</p>
            </div>

            <div className="os-learn-grid">
              {learned.map((l, i) => (
                <article className="os-learn-item" key={l.title} data-reveal style={delay(i % 3, 90)}>
                  <span className={`os-learn-icon tone-${l.tone}`}>
                    <Icon name={l.icon} />
                  </span>
                  <div className="os-learn-copy">
                    <h3>{l.title}</h3>
                    <p>{l.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== WHAT MAKES DIFFERENT ==================== */}
        <section className="op-section" data-purpose="different">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Why Orane</div>
              <h2 className="sec-title">What Makes Orane Different</h2>
              <p className="sec-sub">Experience, industry knowledge, technology and people — working together for real business outcomes.</p>
            </div>

            <div className="os-diff-grid">
              {different.map((d, i) => (
                <article className="os-diff-card" key={d.title} data-reveal style={delay(i, 90)}>
                  <img src={d.image} alt="" aria-hidden="true" loading="lazy" />
                  <div className="os-diff-body">
                    <h3>{d.title}</h3>
                    <p>{d.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== PURPOSE / ROAD AHEAD ==================== */}
        <section className="op-section" data-purpose="purpose">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Purpose &amp; Future</div>
              <h2 className="sec-title">Our Purpose and the Road Ahead</h2>
              <p className="sec-sub">Enterprise AI is changing how organisations operate and make decisions. We are evolving with that change.</p>
            </div>

            <div className="os-purpose">
              <div className="os-purpose-statements">
                <div className="os-purpose-row" data-reveal>
                  <span className="os-purpose-icon"><Icon name="target" /></span>
                  <div className="os-purpose-copy">
                    <div className="os-purpose-label">Our Purpose</div>
                    <div className="os-purpose-strong">Make Enterprises More Valuable.</div>
                  </div>
                </div>
                <div className="os-purpose-row" data-reveal style={delay(1, 120)}>
                  <span className="os-purpose-icon"><Icon name="diamond" /></span>
                  <div className="os-purpose-copy">
                    <div className="os-purpose-label">Our Promise</div>
                    <div className="os-purpose-strong">Real Business. Real Problems. Real Value.</div>
                  </div>
                </div>
              </div>

              <div className="os-purpose-future" data-reveal style={delay(2, 120)}>
                <span className="os-purpose-mark"><Icon name="mark" /></span>
                <h3>Our Future</h3>
                <p>
                  Building on our enterprise transformation heritage, we are working with Axentia, an
                  AI-native enterprise capability company, to explore applied AI, capability development
                  and new approaches to enterprise intelligence.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="os-cta" data-purpose="final-call-to-action" id="contact">
          <img className="os-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="os-cta-copy">
            <h2 data-reveal>Do More. Together.</h2>
            <p data-reveal style={delay(1, 120)}>
              Different people. Different expertise. A common purpose. Real progress for our customers,
              our people and the markets we serve.
            </p>
            <div className="os-actions" data-reveal style={delay(2, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary">
                Talk to Our Team <span>→</span>
              </Link>
              <Link href="/whatwedo" className="fp-buttons fp-hero-outline-btn">
                Explore Our Capabilities <span>→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}